/**
 * Canonical monetary rendering for every amount shown in the invoicing feature:
 * `$1,234.56 MXN`, `$1,234.56 USD`, `€1,234.56 EUR`.
 *
 * The symbol is the currency's own; the ISO code stays because MXN and USD share `$`
 * and the symbol alone cannot tell them apart on a printed line.
 *
 * Why not `Intl.NumberFormat` with `style: 'currency'`: under the `es-MX` locale it
 * renders foreign currencies as `USD 1,234.56` — code first, no symbol — which matches
 * no invoice anyone has ever received.
 *
 * The backend renders the stamped PDF with the same rule
 * (`pdf-generation.support.ts#formatMoney`), so the preview and the final document agree.
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  MXN: '$',
  USD: '$',
  EUR: '€',
};

/** Symbol for a currency; an uncatalogued one falls back to the generic `$`. */
export const currencySymbol = (currency?: string): string =>
  CURRENCY_SYMBOLS[(currency || 'MXN').toUpperCase()] ?? '$';

// The sign goes OUTSIDE the symbol ("-$1,234.56"), as Intl rendered it before this
// module existed and as money is written in Spanish. Reachable from the concepts step:
// a discount larger than the line, or a 100% ISR withholding, both go negative.
const groupAmount = (amount: number): string => {
  const sign = amount < 0 ? '-' : '';
  const value = Math.abs(amount).toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return sign + value;
};

/** Splits the sign off so the symbol can sit between it and the digits. */
const withSymbol = (amount: number, symbol: string): string => {
  const grouped = groupAmount(amount);
  return grouped.startsWith('-')
    ? `-${symbol}${grouped.slice(1)}`
    : `${symbol}${grouped}`;
};

export const formatMoney = (
  amount: number | null | undefined,
  currency: string = 'MXN',
): string => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '-';

  const code = (currency || 'MXN').toUpperCase();
  return `${withSymbol(amount, currencySymbol(code))} ${code}`;
};

/**
 * Same amount without the currency code, for the concepts grid: the totals block right
 * below it carries the code on every line, and repeating it on each row overflows the
 * column. Mirrors how the backend renders that same table in the stamped PDF.
 */
export const formatAmount = (
  amount: number | null | undefined,
  currency: string = 'MXN',
): string => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '-';

  return withSymbol(amount, currencySymbol(currency));
};

/**
 * Whole pesos, no cents and no code, for amounts read at a glance side by side
 * (the month podium): `$27,840`. Only for MXN totals; a CFDI amount goes through
 * `formatMoney`.
 */
export const formatWholePesos = (amount: number): string => {
  const rounded = Math.round(amount);
  const grouped = Math.abs(rounded).toLocaleString('es-MX', { maximumFractionDigits: 0 });
  return rounded < 0 ? `-$${grouped}` : `$${grouped}`;
};
